/**
 * One dialable line in the pad's right-hand column, whatever it came from.
 *
 * React-free, and one shape for all three sources on purpose: a recent call, a
 * client and a company in the business register are three different records
 * that answer the same question - "which number do I press". The column draws
 * this and nothing else, so adding a fourth source is a hook and a tab rather
 * than a fourth branch inside the list.
 */
import { formatPhone, normalizeE164 } from "../../../lib/phone";
import { callCounterpart } from "../../../lib/calls";
import type { CallRow } from "../../../lib/callTypes";
import type { ArListRow } from "../../../lib/ariregister";
import type { BrainBadge } from "../../../lib/siteBrain";
// The file and not the `ui` barrel: this module is imported by a plain node
// test, and the barrel carries React components with it.
import { fmtStamp } from "../../ui/format";

export interface Candidate {
  /** Unique within its own list. Prefixed by source, since ids can collide. */
  key: string;
  /**
   * Who this is, AS THE COLUMN DRAWS IT. A company, a client, or a formatted
   * phone number when the source knows nothing else.
   *
   * ⚠ NOT the name to file a call under. See `companyName` below.
   */
  name: string;
  /**
   * The party's real name, when the source has one.
   *
   * Separate from `name` because `name` has a fallback and this must not: a
   * recent call to a number nobody has identified draws as "+372 5123 4567",
   * and sending that as the company name files the new call under a company
   * called "+372 5123 4567". Two spellings of "we do not know who this is" then
   * exist, and only one of them looks empty.
   */
  companyName?: string;
  /** The number, formatted for reading. Under the name. */
  detail: string;
  /** The number, in E.164. This is what is dialled. */
  number: string;
  /**
   * A short note at the right end of the row: when the last call was, the
   * registry code, whatever the source has that fits in six characters.
   */
  meta?: string;
  /**
   * The company's registry code, when the source knows it.
   *
   * Travels all the way into the call log, so a call placed from the register
   * tab is filed against that company from the moment it is placed rather than
   * being matched up afterwards by its number.
   */
  regCode?: string;
  /**
   * The website this call is about, when the source knows it.
   *
   * Its own field rather than read off `brain.domain`, which is what it was
   * when only the register tab had one. The two are different facts: this is
   * WHICH SITE, and the badge is WHAT THE BRAIN SAID about it. A recent call
   * carries the first without the second, and conflating them would mean either
   * losing the domain or inventing a badge for a company nobody has run.
   */
  domain?: string;
  /**
   * What the development brain already concluded about this company's website.
   *
   * TWO JOBS, and the second one is the reason it is on the candidate rather
   * than fetched by the column:
   *
   * 1. The verdict is readable BEFORE the button is pressed. The brain exists to
   *    answer "is this company worth my afternoon", and until now that answer
   *    lived three clicks away from the green disc.
   * 2. It is where the register tab's `domain` comes from. The register row's
   *    own `webUrl` is free text somebody typed into a form; the badge's domain
   *    was resolved server-side by the one function that already refuses the
   *    e-mail addresses in that column.
   *
   * Only the register source fills it. A recent call carries its domain but no
   * badge, because the call log stores what was dialled rather than what the
   * brain thinks today, and the client book has neither.
   */
  brain?: BrainBadge;
}

/**
 * Recent calls as dialable candidates.
 *
 * Keyed on the COUNTERPARTY throughout, never on `toNumber`: an inbound row
 * keeps our own Twilio line in that column, so reading it listed the office's
 * own number under "recent" and dialling it rang ourselves. A row with no
 * counterparty at all (withheld caller id) is dropped - there is nothing to
 * dial, and a pad entry that cannot be pressed is worse than one line fewer.
 *
 * ⚠ IT CARRIES THE IDENTITY THE ORIGINAL CALL WAS FILED UNDER, and until
 * 2026-08-19 it did not. The call log holds `companyName`, `regCode` and
 * `leadDomain`; this mapping kept only a display name, so ringing somebody back
 * from this list produced a call filed against nobody, with a pre-call briefing
 * that had nothing to look the company up by. The original call knew. The
 * re-dial forgot.
 */
export function recentCandidates(rows: readonly CallRow[]): Candidate[] {
  const out: Candidate[] = [];
  for (const row of rows) {
    const number = callCounterpart(row);
    if (!number) continue;
    out.push({
      key: `call:${row.id}`,
      name: row.companyName ?? formatPhone(number),
      // ⚠ NOT `name`. The line above falls back to the number so the column has
      // something to draw; this one must stay empty, or an unidentified number
      // gets filed as a company called "+372 5123 4567".
      companyName: row.companyName ?? undefined,
      detail: formatPhone(number),
      number,
      meta: fmtStamp(row.startedAt),
      regCode: row.regCode ?? undefined,
      domain: row.leadDomain ?? undefined,
    });
  }
  return out;
}

/**
 * Register search results as dialable candidates.
 *
 * Pure and here rather than in the hook, because the drop rule below is the one
 * thing in the pad that can be wrong in a way nobody sees: a row that quietly
 * vanishes looks exactly like a company that is not in the register.
 */
export function registerCandidates(rows: readonly ArListRow[]): Candidate[] {
  const out: Candidate[] = [];
  for (const row of rows) {
    // The register's phone column is free text and a fair share of it is not one
    // number: "6710268 / 5551234", an address that wandered into the wrong
    // field, a fax. Anything that will not normalize is dropped rather than
    // drawn, for the same reason a withheld caller id is dropped from the recent
    // list - a row that cannot be pressed is worse than one line fewer.
    const number = normalizeE164(row.phone ?? "");
    if (!number) continue;
    out.push({
      key: `ar:${row.regCode}`,
      name: row.name,
      // The register always knows the name, so here the two agree. They are
      // still written separately: the day a row arrives without one, the
      // fallback belongs in `name` alone.
      companyName: row.name,
      detail: formatPhone(number),
      number,
      meta: row.regCode,
      regCode: row.regCode,
      // `?? undefined` and not the raw null: absent and "no run" are one state
      // here, and two spellings of it would mean every reader deciding again.
      brain: row.brain ?? undefined,
      // Only the badge holds a resolved domain. A company with no brain run has
      // a `webUrl` this side must not try to parse into one.
      domain: row.brain?.domain,
    });
  }
  return out;
}

/**
 * Digits only, without the country code, so "+372 5123 4567", "5123 4567" and
 * "372 51234567" all compare as the same subscriber number.
 */
export const digitsOf = (s: string): string => s.replace(/\D/g, "").replace(/^372/, "");

/**
 * Groups of four after the prefix: long enough to stay readable, short enough
 * that it never fights an unusual number shape.
 */
export function fmtTyped(raw: string): string {
  const plus = raw.trim().startsWith("+");
  const d = raw.replace(/\D/g, "");
  if (!d) return plus ? "+" : "";
  const head = plus ? `+${d.slice(0, 3)}` : "";
  const rest = plus ? d.slice(3) : d;
  return [head, ...(rest.match(/.{1,4}/g) ?? [])].filter(Boolean).join(" ");
}

/**
 * The candidate a half-typed number belongs to, or nothing.
 *
 * This is what turns a typed number into a name before the call connects - the
 * one moment it can still be corrected. Order matters: the lists are searched
 * in the order they are given, so the caller decides that somebody rung
 * yesterday outranks a namesake in the client book.
 */
export function matchTyped(typed: string, lists: readonly (readonly Candidate[])[]): Candidate | null {
  const d = digitsOf(typed);
  // Three is the floor at which a prefix means anything at all. Below it every
  // Estonian mobile number matches and the line under the field would name a
  // company at random after the first keypress.
  if (d.length < 3) return null;
  for (const list of lists) {
    const hit = list.find((c) => digitsOf(c.number).startsWith(d));
    if (hit) return hit;
  }
  return null;
}
